import React from "react";

/**
 * Shape preview SVG — boundary segments coloured per BC type.
 * Tight, hairline-led. Used in Geometry list rows and the Geometry editor preview.
 */

const BC_COLORS = {
    ambient: "#01696f",        // teal — most-exposed
    insulated: "#7b8b91",      // warm-grey
    adiabatic: "#2f3a3d",      // slate
    soil: "#6a5d4d",           // earthen
};

const BC_LABELS = {
    ambient: "Ambient air (h ≈ 10 W/m²K)",
    insulated: "Insulated formwork (h ≈ 2 W/m²K)",
    adiabatic: "Adiabatic / symmetry (h = 0)",
    soil: "Soil contact (low h, T_ref)",
};

const segColor = (bc) => BC_COLORS[bc?.bc_type] || "#0e1416";

export const ShapePreview = ({ shape, parameters = {}, boundary_conditions = [], compact = false }) => {
    const bcBy = (label) => boundary_conditions.find((b) => b.segment_label === label);

    const w = compact ? 80 : 280;
    const h = compact ? 60 : 200;

    let body = null;
    if (shape === "mass_pour") {
        const W = parameters.width_m || 2.8;
        const H = parameters.height_m || 2.0;
        const ar = W / H;
        const drawW = ar >= 1 ? w - 24 : (h - 24) * ar;
        const drawH = ar >= 1 ? (w - 24) / ar : h - 24;
        const x0 = (w - drawW) / 2;
        const y0 = (h - drawH) / 2;
        body = (
            <g>
                <rect x={x0} y={y0} width={drawW} height={drawH} fill="#f6f7f6" stroke="#0e1416" strokeWidth="0.9" />
                {/* boundary stripes */}
                <line x1={x0} y1={y0} x2={x0 + drawW} y2={y0} stroke={segColor(bcBy("Top"))} strokeWidth="2.5" />
                <line x1={x0} y1={y0 + drawH} x2={x0 + drawW} y2={y0 + drawH} stroke={segColor(bcBy("Bottom"))} strokeWidth="2.5" />
                <line x1={x0} y1={y0} x2={x0} y2={y0 + drawH} stroke={segColor(bcBy("Sides"))} strokeWidth="2.5" />
                <line x1={x0 + drawW} y1={y0} x2={x0 + drawW} y2={y0 + drawH} stroke={segColor(bcBy("Sides"))} strokeWidth="2.5" />
            </g>
        );
    } else if (shape === "phc_pile") {
        const cx = w / 2;
        const cy = h / 2;
        const ro = Math.min(w, h) / 2 - 12;
        const t = (parameters.wall_thickness_mm || 100) / (parameters.outer_diameter_mm || 600);
        const ri = ro * (1 - t * 2);
        body = (
            <g>
                <circle cx={cx} cy={cy} r={ro} fill="#f6f7f6" stroke={segColor(bcBy("Outer surface"))} strokeWidth="2.5" />
                <circle cx={cx} cy={cy} r={ri} fill="#ffffff" stroke={segColor(bcBy("Inner void surface"))} strokeWidth="2" />
            </g>
        );
    } else if (shape === "slab") {
        const W = parameters.width_m || 4.0;
        const T = parameters.thickness_m || 0.4;
        const ar = W / Math.max(0.05, T);
        const drawH = Math.min(h - 32, (w - 24) / ar);
        const drawW = w - 24;
        const x0 = 12;
        const y0 = (h - drawH) / 2;
        body = (
            <g>
                <rect x={x0} y={y0} width={drawW} height={drawH} fill="#f6f7f6" stroke="#0e1416" strokeWidth="0.9" />
                <line x1={x0} y1={y0} x2={x0 + drawW} y2={y0} stroke={segColor(bcBy("Top"))} strokeWidth="2.5" />
                <line x1={x0} y1={y0 + drawH} x2={x0 + drawW} y2={y0 + drawH} stroke={segColor(bcBy("Bottom"))} strokeWidth="2.5" />
            </g>
        );
    } else if (shape === "wall") {
        const T = parameters.thickness_m || 0.4;
        const H = parameters.height_m || 3.0;
        const ar = T / Math.max(0.05, H);
        const drawW = Math.min(w - 80, (h - 24) * ar * 4);
        const drawH = h - 24;
        const x0 = (w - drawW) / 2;
        const y0 = 12;
        body = (
            <g>
                <rect x={x0} y={y0} width={drawW} height={drawH} fill="#f6f7f6" stroke="#0e1416" strokeWidth="0.9" />
                <line x1={x0} y1={y0} x2={x0} y2={y0 + drawH} stroke={segColor(bcBy("Left face"))} strokeWidth="2.5" />
                <line x1={x0 + drawW} y1={y0} x2={x0 + drawW} y2={y0 + drawH} stroke={segColor(bcBy("Right face"))} strokeWidth="2.5" />
            </g>
        );
    }

    return (
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={compact ? "60" : "auto"} className={compact ? "max-w-[100px]" : ""}>
            {body}
        </svg>
    );
};

export const BC_OPTIONS = BC_LABELS;
export const BC_COLOR_OF = (key) => BC_COLORS[key] || "#0e1416";

/** Segments per shape — used by the geometry editor to drive the BC form. */
export const SEGMENTS_FOR = (shape) => {
    if (shape === "mass_pour") return ["Top", "Bottom", "Sides"];
    if (shape === "phc_pile") return ["Outer surface", "Inner void surface"];
    if (shape === "slab") return ["Top", "Bottom"];
    if (shape === "wall") return ["Left face", "Right face"];
    return [];
};

export default ShapePreview;
